import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, FileDown, Scale } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { ThemeToggleButton } from '../components/ThemeToggle';
import { Button } from '../components/ui/button';
import { EvidenceMarker } from '../components/evidence';

const HERO_PHOTO = `${process.env.PUBLIC_URL}/hero-damage-photo.jpg`;

// Damage regions on the sample photo, as % of its width and height.
const REGIONS = [
  { n: 1, left: 43, top: 9, width: 10, height: 76, delay: 0.55 },
  { n: 2, left: 56, top: 40, width: 8, height: 38, delay: 0.8 },
  { n: 3, left: 54, top: 82, width: 17, height: 9, delay: 1.05 },
];

const HERO_FINDINGS = [
  { n: 1, title: 'Drywall crack', band: 'Severe', cls: 'bg-sev-high-soft text-sev-high' },
  { n: 2, title: 'Water staining', band: 'Moderate', cls: 'bg-sev-med-soft text-sev-med' },
  { n: 3, title: 'Peeling paint', band: 'Minor', cls: 'bg-sev-low-soft text-sev-low' },
];

const STEPS = [
  { title: 'Add the unit', body: "Enter the address and the rooms you'll inspect." },
  { title: 'Photograph each room', body: 'At move-in and again at move-out, from your phone. No extra hardware.' },
  { title: 'Review the findings', body: "Check what was marked, correct anything that's wrong, and add what the model missed." },
  { title: 'Send the report', body: 'Share it with your tenant or export a PDF for your records.' },
];

const FEATURES = [
  { Icon: Camera, title: 'Photos become findings', body: 'Each photo is checked for cracks, stains, burns and missing items, and every finding is marked where it is.' },
  { Icon: Scale, title: 'Wear or damage, decided', body: 'Mark each finding as normal wear or a tenant charge. The same standard applies to every unit you own.' },
  { Icon: FileDown, title: 'Move-in and move-out, side by side', body: 'Compare the same room across inspections, so new damage is easy to tell from what was already there.' },
];

// The sample report is always dark, so it uses fixed colours rather than theme tokens.
const SAMPLE_ROWS = [
  { n: 1, title: 'Drywall crack, full wall height', sev: ['Severe', 'bg-[#3b1512] text-[#ff7a70]'], prov: ['AI detected', 'bg-[#16233d] text-[#93b4f2]'], cost: '$450', charge: 'Normal wear', wear: true },
  { n: 2, title: 'Water staining, lower wall', sev: ['Moderate', 'bg-[#3a2408] text-[#ffa94d]'], prov: ['AI detected, edited', 'bg-[#16233d] text-[#93b4f2]'], cost: '$180', charge: 'Tenant charge', wear: false },
  { n: 3, title: 'Peeling paint at baseboard', sev: ['Minor', 'bg-[#0f2d1e] text-[#5fd49a]'], prov: ['Manual entry', 'bg-[#3a2f00] text-[#f5c400]'], cost: '$35', charge: 'Normal wear', wear: true },
];

const container = 'mx-auto max-w-[1200px] px-6';
const overline = 'm-0 text-xs font-bold uppercase leading-4 tracking-[0.08em]';
const sectionTitle = 'm-0 font-display text-[clamp(32px,3.1vw,44px)] font-extrabold leading-[1.1] tracking-[-0.015em]';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-paper">
        <div className={`${container} flex flex-wrap items-center gap-x-8 gap-y-4 py-4`}>
          <Link to="/" className="flex items-center gap-2.5 font-display text-xl font-extrabold leading-6 text-ink no-underline">
            <BrandLogo className="h-[34px] w-[34px]" />
            RealSpect
          </Link>
          <nav aria-label="Site" className="mr-auto flex flex-wrap gap-x-7 gap-y-2 text-[15px] font-bold">
            <a href="#how" className="text-ink no-underline hover:underline hover:decoration-marker hover:decoration-2 hover:underline-offset-4">How it works</a>
            <a href="#report" className="text-ink no-underline hover:underline hover:decoration-marker hover:decoration-2 hover:underline-offset-4">Sample report</a>
            <a href="#pricing" className="text-ink no-underline hover:underline hover:decoration-marker hover:decoration-2 hover:underline-offset-4">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggleButton />
            <Button asChild variant="ghost" data-testid="nav-login-btn"><Link to="/login">Sign in</Link></Button>
            <Button asChild data-testid="nav-register-btn"><Link to="/register">Start free</Link></Button>
          </div>
        </div>
      </header>

      <main>
        <section className={`${container} flex flex-wrap items-center gap-14 pb-24 pt-[72px]`}>
          <div className="flex min-w-0 flex-[1_1_460px] flex-col gap-6">
            <p className={`${overline} text-ink-muted`}>For landlords and property managers</p>
            <h1 className="m-0 font-display text-[clamp(40px,4.8vw,66px)] font-extrabold leading-[1.02] tracking-[-0.02em]" data-testid="hero-heading">
              Every deposit charge, pinned to a photo.
            </h1>
            <p className="m-0 max-w-[34em] text-[19px] leading-[31px] text-ink-muted">
              Photograph each room at move-in and move-out. RealSpect marks the damage, scores it from 0 to 100, and builds a report where every line item points to the photo it came from.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="hero-cta-btn"><Link to="/register">Start free</Link></Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-demo-btn"><a href="#report">See a sample report</a></Button>
            </div>
            <p className="m-0 text-sm text-ink-muted">Free to start. Every Pro feature for a one-time $15.</p>
          </div>

          <div className="min-w-0 flex-[1_1_520px]">
            <div className="relative aspect-[3/2] overflow-hidden rounded-lg bg-sunken">
              <img
                src={HERO_PHOTO}
                alt="Bedroom wall with a long vertical crack, water staining and peeling paint at the baseboard"
                className="block h-full w-full object-cover"
              />
              <span className="rs-scan" aria-hidden="true" />
              {REGIONS.map((r) => (
                <div
                  key={r.n}
                  aria-hidden="true"
                  className="rs-lock absolute border-2 border-marker shadow-[0_0_0_1px_rgba(14,17,22,0.55)]"
                  style={{ left: `${r.left}%`, top: `${r.top}%`, width: `${r.width}%`, height: `${r.height}%`, '--rs-delay': `${r.delay}s` }}
                >
                  <EvidenceMarker n={r.n} className="rs-drop absolute -left-0.5 -top-7" />
                </div>
              ))}
              <span className="absolute left-2.5 top-2.5 rounded-sm bg-[rgba(14,17,22,0.78)] px-2 py-[3px] font-mono text-[13px] font-semibold leading-[18px] text-[#edeff2]">
                Bedroom 2 · move-out
              </span>
            </div>

            <div className="rs-card-in relative ml-8 -mt-14 flex flex-col gap-2.5 rounded-md border border-line bg-surface px-[18px] py-4 shadow-overlay">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="m-0 text-[15px] font-bold">3 findings · photo 3 of 12</p>
                <p className="m-0 font-mono text-[15px] leading-[22px]">
                  <b className="text-sev-high">68</b><span className="font-semibold text-ink-muted">/100</span>
                </p>
              </div>
              <ul className="m-0 grid list-none grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 p-0 text-[15px] leading-[22px]">
                {HERO_FINDINGS.map((f) => (
                  <li key={f.n} className="contents">
                    <EvidenceMarker n={f.n} size="sm" />
                    <b>{f.title}</b>
                    <span className={`inline-flex h-6 items-center rounded-sm px-2 text-[13px] font-bold ${f.cls}`}>{f.band}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="how" className="border-t border-line">
          <div className={`${container} flex flex-col gap-12 py-24`}>
            <div className="flex max-w-[760px] flex-col gap-3">
              <p className={`${overline} text-ink-muted`}>How it works</p>
              <h2 className={`${sectionTitle} rs-reveal`}>From move-out photos to a finished report, in one visit.</h2>
            </div>
            <ol className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(230px,100%),1fr))] gap-8 p-0">
              {STEPS.map((s, i) => (
                <li key={s.title} className="rs-reveal rs-reveal-step flex flex-col gap-3">
                  <EvidenceMarker n={i + 1} size="lg" />
                  <h3 className="m-0 font-display text-[22px] font-bold leading-7">{s.title}</h3>
                  <p className="m-0 text-ink-muted">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="report" className="border-y border-line bg-[#0e1116] text-[#edeff2] dark:bg-[#07090c]">
          <div className={`${container} flex flex-wrap items-start gap-14 py-24`}>
            <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-6">
              <p className={`${overline} text-[#9aa3af]`}>Sample report</p>
              <h2 className={`${sectionTitle} rs-reveal`}>What your tenant sees.</h2>
              <p className="m-0 text-[19px] leading-[31px] text-[#9aa3af]">A report a tenant can check line by line, without calling you to ask what a charge is for.</p>
              <dl className="m-0 flex flex-col gap-5">
                {[
                  ['Numbered markers', 'Each line item carries the number of the marker on the photo.'],
                  ['Who found it', 'AI detected, edited by you, or entered by you. Nothing is hidden.'],
                  ['Wear or charge', "Normal wear is shown and struck through, so tenants see what you didn't charge."],
                  ['How it was scored', 'The analysis record at the foot of the report shows the method and when the photos were taken.'],
                ].map(([term, def]) => (
                  <div key={term} className="flex flex-col gap-1 border-t border-[#2a303a] pt-4">
                    <dt className="font-bold">{term}</dt>
                    <dd className="m-0 text-[#9aa3af]">{def}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="flex min-w-0 flex-[1.4_1_560px] flex-col gap-5 rounded-lg border border-[#2a303a] bg-[#161a21] p-7">
              <div className="flex flex-wrap justify-between gap-5">
                <div className="flex min-w-0 flex-col gap-1">
                  <p className={`${overline} text-[#9aa3af]`}>Move-out inspection</p>
                  <h3 className="m-0 font-display text-[26px] font-bold leading-8">Oakwood Apartments, Unit 4B</h3>
                  <p className="m-0 text-sm leading-[22px] text-[#9aa3af]">Example report</p>
                </div>
                <div className="flex flex-col gap-2">
                  <p className="m-0 flex items-baseline gap-2 font-mono">
                    <span className="text-[56px] font-bold leading-[56px] text-[#ff7a70]">68</span>
                    <span className="text-[15px] font-semibold text-[#9aa3af]">/100</span>
                  </p>
                  <span aria-hidden="true" className="relative grid h-2 w-[180px] grid-cols-[40fr_20fr_40fr] gap-0.5">
                    <span className="border border-[#5fd49a] bg-[#0f2d1e]" />
                    <span className="border border-[#ffa94d] bg-[#3a2408]" />
                    <span className="border border-[#ff7a70] bg-[#3b1512]" />
                    <i className="rs-tick absolute -top-[5px] left-[68%] -ml-px h-[18px] w-[3px] bg-[#edeff2]" />
                  </span>
                  <p className="m-0 text-sm font-bold">Major damage</p>
                </div>
              </div>

              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-px overflow-hidden rounded-md border border-[#2a303a] bg-[#2a303a]">
                {[['Findings', '3'], ['Tenant charges', '$180'], ['Photos', '12']].map(([k, v]) => (
                  <div key={k} className="bg-[#161a21] px-3.5 py-3">
                    <p className={`${overline} text-[#9aa3af]`}>{k}</p>
                    <p className="m-0 mt-0.5 font-mono text-[22px] font-bold leading-7">{v}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                {SAMPLE_ROWS.map((r) => (
                  <div key={r.n} className="rs-row-in grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2 rounded-md border border-[#2a303a] px-4 py-3.5">
                    <EvidenceMarker n={r.n} className="!bg-[#f5c400] !text-[#14171c]" />
                    <div className="min-w-0">
                      <p className="m-0 font-bold">{r.title}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <span className={`inline-flex h-6 items-center rounded-sm px-2 text-[13px] font-bold ${r.sev[1]}`}>{r.sev[0]}</span>
                        <span className={`inline-flex h-6 items-center rounded-sm px-2 text-[13px] font-bold ${r.prov[1]}`}>{r.prov[0]}</span>
                      </div>
                    </div>
                    <p className="m-0 text-right font-mono text-[15px] font-bold">
                      <span className={r.wear ? 'font-semibold text-[#9aa3af] line-through' : ''}>{r.cost}</span>
                      <span className="block font-body text-[13px] font-normal text-[#9aa3af]">{r.charge}</span>
                    </p>
                  </div>
                ))}
              </div>

              <dl className="m-0 flex flex-wrap gap-x-5 gap-y-1.5 rounded-md bg-[#0a0c10] px-3.5 py-2.5 font-mono text-[13px] font-semibold leading-[18px]">
                {[['mode', 'llm+cv'], ['regions', 'vlm+yolo'], ['captured', '2026-07-15 14:22 CDT']].map(([k, v]) => (
                  <div key={k}><dt className="inline text-[#9aa3af]">{k}</dt> <dd className="m-0 inline">{v}</dd></div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section>
          <div className={`${container} flex flex-col gap-12 py-24`}>
            <h2 className={`${sectionTitle} rs-reveal max-w-[760px]`}>Fewer arguments about the deposit.</h2>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-10">
              {FEATURES.map(({ Icon, title, body }) => (
                <div key={title} className="rs-rule rs-reveal flex flex-col gap-2.5">
                  <Icon className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="m-0 font-display text-[22px] font-bold leading-7">{title}</h3>
                  <p className="m-0 text-ink-muted">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="border-t border-line">
          <div className={`${container} flex flex-col gap-10 py-24`}>
            <h2 className={`${sectionTitle} rs-reveal`}>Pricing</h2>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(300px,100%),1fr))] gap-6">
              <div className="flex flex-col gap-3.5 rounded-md border border-line bg-surface p-7">
                <p className={`${overline} text-ink-muted`}>Free</p>
                <p className="m-0 font-display text-[40px] font-extrabold leading-[44px]">$0</p>
                <p className="m-0 text-ink-muted">Try RealSpect on your own units.</p>
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {['Photo analysis and damage scores', 'Findings with wear or charge decisions'].map((t) => (
                    <li key={t} className="flex items-start gap-2"><Check className="mt-1 h-4 w-4 shrink-0 text-sev-low" strokeWidth={2} aria-hidden="true" />{t}</li>
                  ))}
                </ul>
                <Button asChild variant="outline" className="mt-auto self-start"><Link to="/register">Start free</Link></Button>
              </div>
              <div className="flex flex-col gap-3.5 rounded-md border-2 border-ink bg-surface p-7" data-testid="pricing-pilot-pro">
                <p className={`${overline} text-ink-muted`}>Pilot Pro</p>
                <p className="m-0 font-display text-[40px] font-extrabold leading-[44px]">$15 <span className="font-body text-base font-normal text-ink-muted">one time</span></p>
                <p className="m-0 text-ink-muted">Every Pro feature while we're in pilot, with no subscription.</p>
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {['Everything in Free', 'PDF inspection reports', 'Tenant share links', 'Move-in and move-out comparison', 'Through launch plus 12 months'].map((t) => (
                    <li key={t} className="flex items-start gap-2"><Check className="mt-1 h-4 w-4 shrink-0 text-sev-low" strokeWidth={2} aria-hidden="true" />{t}</li>
                  ))}
                </ul>
                <p className="m-0 text-sm text-ink-muted">Full refund within 14 days.</p>
                <Button asChild className="mt-auto self-start"><Link to="/register">Get Pilot Pro</Link></Button>
              </div>
              <div className="flex flex-col gap-3.5 rounded-md border border-line bg-surface p-7">
                <p className={`${overline} text-ink-muted`}>Team</p>
                <p className="m-0 font-display text-[40px] font-extrabold leading-[44px]">Custom</p>
                <p className="m-0 text-ink-muted">For portfolios and property management teams, with scaled inspections, reporting automation and integrations.</p>
                <Button asChild variant="outline" className="mt-auto self-start"><Link to="/register">Get started</Link></Button>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-marker text-on-marker">
          <div className={`${container} flex flex-wrap items-center justify-between gap-8 py-[88px]`}>
            <h2 className="rs-reveal m-0 max-w-[720px] font-display text-[clamp(32px,3.6vw,52px)] font-extrabold leading-[1.05] tracking-[-0.02em]">
              Have a move-out coming up? Inspect it with RealSpect.
            </h2>
            <Link
              to="/register"
              data-testid="cta-register-btn"
              className="inline-flex h-14 items-center rounded-md bg-[#14171c] px-8 text-[17px] font-bold text-[#f2f3f5] no-underline hover:opacity-90"
            >
              Start free
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-line">
        <div className={`${container} flex flex-wrap items-center justify-between gap-4 py-7 text-sm text-ink-muted`}>
          <span className="flex items-center gap-2.5 font-display text-base font-extrabold text-ink">
            <BrandLogo className="h-[26px] w-[26px]" />RealSpect
          </span>
          <span>Evidence-grade property inspections.</span>
        </div>
      </footer>
    </div>
  );
}
